import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { setAuth } from '../Redux/authSlice';
import { useDispatch } from 'react-redux';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

interface Section {
  id: number;
  name: string;
  class_associated?: number;
  class_name?: string;
}

interface Class {
  id: number;
  name: string;
}

const Sections: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [newSectionName, setNewSectionName] = useState('');
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [editableSection, setEditableSection] = useState<Section | null>(null);
  const [sectionToDelete, setSectionToDelete] = useState<Section | null>(null);

  const dispatch = useDispatch();

  // Get cookie by name
  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  const [userRole, setUserRole] = useState<string>('');

  // Authentication check
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const res = await axios.get('/users/user');
        setUserRole(res.data.user.role);
        dispatch(setAuth(true));
      } catch (e) {
        dispatch(setAuth(false));
      }
    };
    fetchUserData();
  }, [dispatch]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectionsRes, classesRes] = await Promise.all([
          axios.get('/Branches/sections/'),
          axios.get('/Branches/classes/')
        ]);

        // Enhance sections with class names
        const enhancedSections = sectionsRes.data.map((section: Section) => {
          const classInfo = classesRes.data.find((c: Class) => c.id === section.class_associated);
          return {
            ...section,
            class_name: classInfo?.name || 'Unknown'
          };
        });

        setSections(enhancedSections);
        setClasses(classesRes.data.results || classesRes.data);
        setLoading(false);
      } catch (err) {
        setError('Error fetching data');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Create new section
  const handleCreate = async () => {
    if (!newSectionName.trim() || !selectedClassId) return;

    try {
      const response = await axios.post(
        '/Branches/sections/',
        {
          name: newSectionName,
          class_associated: selectedClassId
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      // Find the class name for the new section
      const classInfo = classes.find(c => c.id === selectedClassId);
      const newSectionWithClass = {
        ...response.data,
        class_name: classInfo?.name || 'Unknown'
      };

      setSections([...sections, newSectionWithClass]);
      setNewSectionName('');
      setSelectedClassId(null);
      setShowCreateModal(false);
    } catch (error: any) {
      console.error('Error creating section:', error);
      setError('Failed to create section');
    }
  };

  // Edit section
  const handleUpdate = async () => {
    if (!editableSection) return;
    try {
      const response = await axios.patch(
        `/Branches/sections/${editableSection.id}/`,
        { name: editableSection.name },
        { headers: { 'Content-Type': 'application/json' } }
      );

      // Preserve the class information when updating
      const updatedSection = {
        ...response.data,
        class_associated: editableSection.class_associated,
        class_name: editableSection.class_name
      };

      setSections(sections.map(s => (s.id === editableSection.id ? updatedSection : s)));
      setEditableSection(null);
      setShowEditModal(false);
    } catch (error: any) {
      console.error('Error updating section:', error);
      setError('Failed to update section');
    }
  };

  // Delete section
  const confirmDelete = async () => {
    if (!sectionToDelete) return;
    try {
      await axios.delete(`/Branches/sections/${sectionToDelete.id}/`);
      setSections(sections.filter(s => s.id !== sectionToDelete.id));
      setSectionToDelete(null);
      setShowDeleteModal(false);
    } catch (error: any) {
      console.error('Error deleting section:', error);
      setError('Failed to delete section');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-content d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <div className="alert alert-danger text-center shadow-sm border-0">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      {/* Header and Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-primary mb-1">Stream Management</h2>
          <p className="text-muted mb-0">Manage streams and their associated classes.</p>
        </div>
        {userRole !== 'teacher' && (
          <button
            className="btn btn-primary d-flex align-items-center shadow hover-scale rounded-pill px-4"
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus className="me-2" /> Add Stream
          </button>
        )}
      </div>

      {/* Sections Table */}
      <div className="card-modern shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4 py-4 text-secondary text-uppercase small fw-bold">ID</th>
                  <th className="py-4 text-secondary text-uppercase small fw-bold">Stream Name</th>
                  <th className="py-4 text-secondary text-uppercase small fw-bold">Class</th>
                  <th className="pe-4 py-4 text-end text-secondary text-uppercase small fw-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sections.length > 0 ? (
                  sections.map(section => (
                    <tr key={section.id} style={{ height: '60px' }}>
                      <td className="ps-4 fw-medium text-muted">#{section.id}</td>
                      <td className="fw-bold text-dark fs-5">{section.name}</td>
                      <td>
                        <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill fw-normal">
                          {section.class_name || 'N/A'}
                        </span>
                      </td>
                      <td className="pe-4 text-end">
                        {userRole !== 'teacher' && (
                          <>
                            <button
                              className="btn btn-light btn-sm text-primary me-2 rounded-circle p-2"
                              onClick={() => {
                                setEditableSection(section);
                                setShowEditModal(true);
                              }}
                              title="Edit"
                            >
                              <FaEdit size={16} />
                            </button>
                            <button
                              className="btn btn-light btn-sm text-danger rounded-circle p-2"
                              onClick={() => {
                                setSectionToDelete(section);
                                setShowDeleteModal(true);
                              }}
                              title="Delete"
                            >
                              <FaTrash size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-5 text-muted">
                      <i className="bi bi-diagram-2 fs-1 d-block mb-3 opacity-50"></i>
                      No streams found. Add one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Section Modal */}
      <div className={`modal fade ${showCreateModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-4">
            <div className="modal-header border-bottom-0 pb-0">
              <h5 className="modal-title fw-bold text-primary">Add New Stream</h5>
              <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
            </div>
            <div className="modal-body pt-4">
              <div className="mb-4">
                <label className="form-label fw-bold text-secondary">Class</label>
                <select
                  className="form-select form-select-lg bg-light border-0"
                  value={selectedClassId || ''}
                  onChange={(e) => setSelectedClassId(Number(e.target.value))}
                  required
                >
                  <option value="" disabled>Select a class</option>
                  {classes.map(classItem => (
                    <option key={classItem.id} value={classItem.id}>
                      {classItem.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label fw-bold text-secondary">Stream Name</label>
                <input
                  type="text"
                  className="form-control form-control-lg bg-light border-0"
                  placeholder="e.g. North"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="modal-footer border-top-0">
              <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary rounded-pill px-4" onClick={handleCreate} disabled={!newSectionName || !selectedClassId}>
                Create Stream
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Section Modal */}
      <div className={`modal fade ${showEditModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-4">
            <div className="modal-header border-bottom-0 pb-0">
              <h5 className="modal-title fw-bold text-primary">Edit Stream</h5>
              <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
            </div>
            <div className="modal-body pt-4">
              <div className="mb-4">
                <label className="form-label fw-bold text-secondary">Associated Class</label>
                <input
                  type="text"
                  className="form-control form-control-lg bg-light border-0"
                  value={editableSection?.class_name || 'N/A'}
                  disabled
                />
                <div className="form-text">Class association cannot be changed here.</div>
              </div>
              <div className="mb-2">
                <label className="form-label fw-bold text-secondary">Stream Name</label>
                <input
                  type="text"
                  className="form-control form-control-lg bg-light border-0"
                  value={editableSection?.name || ''}
                  onChange={(e) => setEditableSection(editableSection ? { ...editableSection, name: e.target.value } : null)}
                  required
                />
              </div>
            </div>
            <div className="modal-footer border-top-0">
              <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary rounded-pill px-4" onClick={handleUpdate}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-4">
            <div className="modal-body p-4 text-center">
              <div className="bg-danger bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                <FaTrash className="text-danger fs-3" />
              </div>
              <h4 className="fw-bold text-dark">Delete Stream?</h4>
              <p className="text-muted mb-4">
                Are you sure you want to delete <strong>{sectionToDelete?.name}</strong>?<br />
                This action cannot be undone.
              </p>
              {sectionToDelete?.class_name && (
                <div className="alert alert-warning py-2 mb-4 small">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  This stream belongs to <strong>{sectionToDelete.class_name}</strong>
                </div>
              )}
              <div className="d-flex justify-content-center gap-2">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger rounded-pill px-4" onClick={confirmDelete}>Yes, Delete</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sections;